import React from 'react';
import './Header.css';

interface HeaderProps {
  currentIndex: number;
  isRollFinished: boolean;
  totalCoins?: number;
  rollType?: string;
}

interface RollHeaderInfo {
  title: string;
  themeClass?: string;
}

export function getRollHeaderInfo(rollType?: string): RollHeaderInfo {
  const type = rollType || 'standard_2euro';

  // Standard rolls
  const standardRolls: Record<string, { name: string; themeClass: string }> = {
    standard_2euro: { name: '2€ Standard Roll', themeClass: 'roll-theme-standard_2euro' },
    standard_1euro: { name: '1€ Standard Roll', themeClass: 'roll-theme-standard_1euro' },
    standard_50cent: { name: '50¢ Standard Roll', themeClass: 'roll-theme-standard_50cent' },
    standard_20cent: { name: '20¢ Standard Roll', themeClass: 'roll-theme-standard_20cent' },
    standard_10cent: { name: '10¢ Standard Roll', themeClass: 'roll-theme-standard_10cent' },
    standard_5cent: { name: '5¢ Standard Roll', themeClass: 'roll-theme-standard_5cent' },
    standard_2cent: { name: '2¢ Standard Roll', themeClass: 'roll-theme-standard_2cent' },
    standard_1cent: { name: '1¢ Standard Roll', themeClass: 'roll-theme-standard_1cent' },
  };

  if (standardRolls[type]) {
    return {
      title: standardRolls[type].name,
      themeClass: standardRolls[type].themeClass,
    };
  }

  // Regional rolls
  const regionalRolls: Record<string, { name: string; flag: string; denom: string }> = {
    regional_germany_2euro: { name: 'Deutsche Bundesbank', flag: '🇩🇪', denom: '2€' },
    regional_france_2euro: { name: 'Banque de France', flag: '🇫🇷', denom: '2€' },
    regional_italy_2euro: { name: 'Banca d\'Italia', flag: '🇮🇹', denom: '2€' },
    regional_spain_2euro: { name: 'Banco de España', flag: '🇪🇸', denom: '2€' },
    regional_bulgaria_2euro: { name: 'Bulgarian National Bank', flag: '🇧🇬', denom: '2€' },
  };

  if (regionalRolls[type]) {
    const item = regionalRolls[type];
    return {
      title: `${item.flag} ${item.name} - ${item.denom} Roll`,
      themeClass: 'roll-theme-regional',
    };
  }

  // Generic fallback if regional roll id has pattern regional_<country>_<denom>
  if (type.startsWith('regional_')) {
    const parts = type.replace('regional_', '').split('_');
    const rawCountry = parts[0] || 'Europe';
    const rawDenom = parts[1] || '2euro';

    const countryName = rawCountry.charAt(0).toUpperCase() + rawCountry.slice(1);
    const denomLabel = rawDenom === '2euro' ? '2€' : rawDenom === '1euro' ? '1€' : rawDenom;

    const countryFlags: Record<string, string> = {
      germany: '🇩🇪',
      france: '🇫🇷',
      italy: '🇮🇹',
      spain: '🇪🇸',
      bulgaria: '🇧🇬',
      austria: '🇦🇹',
      belgium: '🇧🇪',
      netherlands: '🇳🇱',
      portugal: '🇵🇹',
      greece: '🇬🇷',
      finland: '🇫🇮',
      ireland: '🇮🇪',
      slovakia: '🇸🇰',
      slovenia: '🇸🇮',
      cyprus: '🇨🇾',
      malta: '🇲🇹',
      luxembourg: '🇱🇺',
      estonia: '🇪🇪',
      latvia: '🇱🇻',
      lithuania: '🇱🇹',
      croatia: '🇭🇷',
      andorra: '🇦🇩',
      monaco: '🇲🇨',
      san_marino: '🇸🇲',
      vatican: '🇻🇦',
    };

    const flag = countryFlags[rawCountry.toLowerCase()] || '🇪🇺';
    return {
      title: `${flag} ${countryName} - ${denomLabel} Regional Roll`,
      themeClass: 'roll-theme-regional',
    };
  }

  return {
    title: 'Euro Hunting Simulator',
  };
}

export const Header: React.FC<HeaderProps> = ({
  currentIndex,
  isRollFinished,
  totalCoins = 25,
  rollType
}) => {
  const counterText = isRollFinished
    ? `Coin Roll Finished (${totalCoins} / ${totalCoins})`
    : `Coin ${currentIndex + 1} / ${totalCoins}`;

  const headerInfo = getRollHeaderInfo(rollType);

  return (
    <>
      <h1 className={`title ${headerInfo.themeClass || ''}`}>
        {headerInfo.title}
      </h1>
      <div className="subtitle" id="coinCounter">
        {counterText}
      </div>
    </>
  );
};

